import React from "react";
import { cn } from "../../../../lib/utils";

const EditableExpandableTextCell: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  maxWidth?: number | string;
  className?: string;
}> = ({ value = "", onChange, maxWidth = "421px", className }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  const editableRef = React.useRef<HTMLDivElement>(null);
  const prevEditingRef = React.useRef(false);

  const isEmpty = !value || value === "";

  const widthConstraint =
    typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;

  const shouldShowToggle = !isEditing && value.length > 150;

  const handleInput = () => {
    let newValue = editableRef.current?.innerText ?? "";

    if (newValue === "\n") {
      newValue = "";
    }

    if (newValue !== value) {
      onChange?.(newValue);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  React.useEffect(() => {
    if (isEditing && !prevEditingRef.current && editableRef.current) {
      editableRef.current.innerText = value || "";
      editableRef.current.focus();

      const range = document.createRange();
      const selection = window.getSelection();

      range.selectNodeContents(editableRef.current);
      range.collapse(false);

      selection?.removeAllRanges();
      selection?.addRange(range);
    }

    if (
      !isEditing &&
      editableRef.current &&
      editableRef.current.innerText !== value
    ) {
      editableRef.current.innerText = value || "";
    }

    prevEditingRef.current = isEditing;
  }, [isEditing, value]);

  return (
    <div
      className="relative h-full flex flex-col justify-center"
      style={{
        minWidth: widthConstraint,
        width: "100%",
      }}
    >
      {isEditing ? (
        <div className="relative flex items-center">
          {isEmpty && (
            <span className={cn("absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none font-sans font-normal text-sm leading-5 text-[#0A0A0A]/60", className)}>
              Add
            </span>
          )}

          <div
            ref={editableRef}
            contentEditable
            suppressContentEditableWarning={true}
            onBlur={handleBlur}
            onInput={handleInput}
            className={cn("w-full font-sans leading-5 text-sm text-[#0A0A0A] outline-none bg-transparent", className)}
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              ...(!className && {
                lineHeight: "1.25rem",
                minHeight: "1.5rem",
              }),
              overflowY: "auto",
            }}
          />
        </div>
      ) : (
        <>
          <div
            className={cn("w-full font-sans font-normal text-sm leading-5 text-[#0A0A0A] cursor-text bg-transparent rounded", className)}
            style={{
              display: isEmpty ? "flex" : "-webkit-box",
              alignItems: isEmpty ? "center" : undefined,
              WebkitLineClamp: expanded ? "unset" : 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              ...(!className && {
                lineHeight: "1.25rem",
                minHeight: "1.5rem",
              }),
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
            onClick={() => setIsEditing(true)}
          >
            {isEmpty ? (
              <span className="font-sans font-normal text-sm leading-5 text-[#0A0A0A]/60">
                Add
              </span>
            ) : (
              value
            )}
          </div>

          {shouldShowToggle && (
            <button
              type="button"
              className={cn("text-[#3551F3] text-sm font-normal leading-5 underline underline-offset-auto decoration-auto focus:outline-none self-start", className)}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((prev) => !prev);
              }}
            >
              {expanded ? "See less" : "See more"}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default  EditableExpandableTextCell